import { TRACK_EVENTS } from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { DocsFormDataMapped, DocsFormName } from "core/types";
import { useCallback } from "react";
import { useTracking } from "react-tracking";
import { OPERATOR_EXECUTOR_PORT, useOperatorAgent } from ".";

export type OperatorExecutorField = {
  identifier: string;
  value: string;
};

export async function postToOperatorExecutor({
  careseekerId,
  fields,
  idempotencyKey,
  port = OPERATOR_EXECUTOR_PORT,
  templateIdentifier,
}: {
  careseekerId: string;
  fields: OperatorExecutorField[];
  idempotencyKey: string;
  port?: number;
  templateIdentifier: string;
}): Promise<void> {
  const token = tokenAccessor();
  if (!token) {
    throw new Error(
      "Not logged in to Recare — can't trigger the Operator executor.",
    );
  }

  const response = await fetch(`http://127.0.0.1:${port}/execute`, {
    body: JSON.stringify({
      careseeker_id: careseekerId,
      fields,
      idempotency_key: idempotencyKey,
      template_identifier: templateIdentifier,
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(
      `Operator executor error: ${response.status} ${response.statusText}`,
    );
  }
}

export function useTriggerOperatorExecutor() {
  const { active, port } = useOperatorAgent();
  const { trackEvent } = useTracking();

  return useCallback(
    async <T extends DocsFormName>({
      careseekerId,
      fieldOrder,
      formData,
      idempotencyKey,
      templateIdentifier,
    }: {
      careseekerId: number | undefined;
      fieldOrder?: Array<keyof DocsFormDataMapped<T>>;
      formData: DocsFormDataMapped<T>;
      idempotencyKey: number;
      templateIdentifier: T;
    }): Promise<boolean> => {
      if (!active || !careseekerId) return true;

      const orderedKeys =
        fieldOrder ??
        (Object.keys(formData) as Array<keyof DocsFormDataMapped<T>>);

      const fields: OperatorExecutorField[] = orderedKeys
        .filter((identifier) => {
          const value = formData[identifier];
          return value != null && String(value).trim() !== "";
        })
        .map((identifier) => ({
          identifier: String(identifier),
          value: String(formData[identifier]).trim(),
        }));

      try {
        await postToOperatorExecutor({
          careseekerId: String(careseekerId),
          fields,
          idempotencyKey: String(idempotencyKey),
          port,
          templateIdentifier,
        });
        trackEvent({
          form_name: templateIdentifier,
          name: TRACK_EVENTS.OPERATOR_EXECUTOR_TRIGGER_SUCCESS,
        });
        return true;
      } catch (err) {
        console.error("Failed to trigger Operator executor:", err);
        trackEvent({
          error: err instanceof Error ? err.message : String(err),
          form_name: templateIdentifier,
          name: TRACK_EVENTS.OPERATOR_EXECUTOR_TRIGGER_ERROR,
        });
        return false;
      }
    },
    [active, port, trackEvent],
  );
}
